import PropTypes from "prop-types";
import { memo, useEffect, useMemo, useRef } from "react";

// Default sections
import { MiniDealBrand } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useReferenceState } from "@/Common/hooks/ReferenceState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import TileSelectButton from "@/Layouts/components/Buttons/TileSelectButton";

// Custom components
import MiniDealModel from "../MiniDealModel";

// MUI
import { Grid, Grow } from "@mui/material";
import { isNil } from "lodash";
import moment from "moment";
import { getRelativeYear } from "@/Common/utils/DateUtils";
import { hasOverlap } from "@/Common/utils/ValidationUtils";

// Constants
const MAX_INPUT_VALUE = 1000000;

const useAvailableBrands = (brands = [], selectedBrands = []) => {
	return brands
		?.filter((brand) => !selectedBrands.some((item) => item.make === brand.make && !item?.model))
		.map((brand) => ({ _id: brand.make, make: brand.make }));
};

const getEmptyDuplicated = (addons = []) => addons.filter((item) => !item?.model);

const getModelsToCompare = (miniDeals, tab = "") => {
	if (!tab || !miniDeals) {
		return [];
	}
	const { addons = [] } = miniDeals || {};

	if (!addons?.length) {
		return [];
	}

	const emptyAddons = getEmptyDuplicated(addons);
	if (tab === "new" && emptyAddons?.length) {
		return [...addons, ...emptyAddons];
	}

	return [...addons];
};

function MiniDealContainerMemo(props) {
	const {
		tab = "",
		miniDeals = null,
		brands = [],
		loading = false,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, validate } = useValidations();
	const modelErrors = errors?.get("addons");

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onMiniDealsUpdate = (updatedMiniDeals) => {
		const valid = Boolean(isValid && isValidModel);
		onActionValidation?.(valid);

		if (valid && wasUpdated.current) {
			const updated = {
				default: [...updatedMiniDeals.default],
				addons: [...updatedMiniDeals.addons],
			};

			if (tab === "preOwned") {
				updated.default = { ...updatedMiniDeals.default[0] };
			}

			onActionUpdate?.(updated);
		}
	};

	// States
	const [innerMiniDeals, setMiniDeals] = useCallbackState(
		{
			defaults: [],
			addons: [],
		},
		onMiniDealsUpdate
	);

	const [isValidDefault, setIsValidDefault, validDefaultList, recheckValidDefault] = useReferenceState(true, []);
	const [isValidAddons, setIsValidAddons, validAddonsList, recheckValidAddons] = useReferenceState(true, []);

	const availableBrands = useAvailableBrands(brands, innerMiniDeals.addons);

	const updateMiniDeals = (content) => {
		wasUpdated.current = true;
		setMiniDeals(content);
	};

	const isValidModel = useMemo(() => isValidDefault && isValidAddons, [isValidDefault, isValidAddons]);

	const handleUpdateMiniDealModel = (area, index, content) => {
		const updatedMiniDeals = { ...innerMiniDeals };
		updatedMiniDeals[area][index] = { ...content };
		updateMiniDeals(updatedMiniDeals);
	};

	const handleAddMiniDeal = function (value) {
		const updatedMiniDeals = { ...innerMiniDeals };
		updatedMiniDeals?.addons?.push(MiniDealBrand(value.make));
		updateMiniDeals(updatedMiniDeals);
	};

	const handleDeleteAddons = (index) => {
		const updatedMiniDeals = { ...innerMiniDeals };
		if (index !== -1) {
			updatedMiniDeals.addons.splice(index, 1);
			validAddonsList[index] = true;
			recheckValidAddons();
		}
		updateMiniDeals(updatedMiniDeals);
	};

	useMemo(() => {
		const modelsToCompare = getModelsToCompare(innerMiniDeals, tab);

		if (tab === "new" && !modelsToCompare?.length) {
			modelsToCompare.push({});
		}

		if (modelsToCompare?.length) {
			validate([
				{
					definition: {
						useKey: "addons",
						operators: {
							conditions: [
								{
									type: "HIERARCHICAL_UNIQUE",
									hierarchy: ["make", "model", "modelYears"],
								},
								{
									type: "INTERSECTION",
									on: [
										{
											key: "modelYears.left",
											useModifier: {
												function: "getRelativeYear",
												when: { key: "modelYears.rangeType", value: "RELATIVE" },
											},
										},
										{
											key: "modelYears.right",
											useModifier: {
												function: "getRelativeYear",
												when: { key: "modelYears.rangeType", value: "RELATIVE" },
											},
										},
									],
									useModifier: { function: "hasOverlap" },
									logic: "not",
									applyIf: {
										type: "HIERARCHICAL_UNIQUE",
										hierarchy: ["make", "model"],
										logic: "not",
									},
								},
							],
							modifiers: {
								hasOverlap,
								getRelativeYear,
							},
							maxInvalidConditions: 2,
						},
						errorsMap: ["make", "model", "modelYears"],
						message: "Invalid model definition",
						validationType: "COMPARATIVE",
					},
					section: modelsToCompare || [],
				},
			]);
		}
	}, [tab, innerMiniDeals, validate]);

	useEffect(() => {
		return () => {
			setIsValidDefault(true);
			setIsValidAddons(true);
		};
	}, [setIsValidDefault, setIsValidAddons]);

	useEffect(() => {
		const miniDealsDefinition = {
			default: [],
			addons: [],
		};

		if (tab === "preOwned") {
			if (miniDeals?.default) {
				miniDealsDefinition.default.push(miniDeals.default);
			}
		} else {
			miniDealsDefinition.default = miniDeals.default || [];
		}

		if (miniDeals?.addons?.length) {
			miniDealsDefinition.addons = miniDeals.addons;
		}

		setMiniDeals(miniDealsDefinition);

		return () => {
			setMiniDeals({
				default: [],
				addons: [],
			});
			wasUpdated.current = false;
		};
	}, [tab, miniDeals, setMiniDeals]);

	const isSpecificModelsAllowed = tab === "preOwned";

	return (
		<Grid container spacing={2} sx={{ maxWidth: "1160px", marginTop: "20px" }}>
			{innerMiniDeals?.default?.map((deal, key) => {
				const itemKey = `mini_deal_default_${key}`;
				return (
					<Grow in timeout={500} key={itemKey}>
						<Grid item>
							<MiniDealModel
								index={key}
								data={deal}
								readOnly={readOnly}
								onActionUpdate={(miniDeal) => handleUpdateMiniDealModel("default", key, miniDeal)}
								onActionValidation={(value) => {
									validDefaultList[key] = value;
									recheckValidDefault();
								}}
							/>
						</Grid>
					</Grow>
				);
			})}

			{innerMiniDeals?.addons?.map((deal, key) => {
				const itemKey = `mini_deal_addons_${key}`;
				return (
					<Grow in timeout={500} key={itemKey}>
						<Grid item>
							<MiniDealModel
								index={key}
								data={deal}
								deletable
								modelSelectable={true}
								error={modelErrors?.[key]?.model || false}
								allowSpecificModels={isSpecificModelsAllowed}
								readOnly={readOnly}
								onActionDelete={handleDeleteAddons}
								onActionUpdate={(miniDeal) => handleUpdateMiniDealModel("addons", key, miniDeal)}
								onActionValidation={(value) => {
									validAddonsList[key] = value;
									recheckValidAddons();
								}}
							/>
						</Grid>
					</Grow>
				);
			})}

			<Grid item>
				<TileSelectButton
					tooltip={"Add Model"}
					data={availableBrands}
					sortBy={"make"}
					showBy={"make"}
					onSelect={handleAddMiniDeal}
					disabled={loading || readOnly || !(isValid && isValidModel)}
					loading={loading}
				/>
			</Grid>
		</Grid>
	);
}
MiniDealContainerMemo.propTypes = {
	tab: PropTypes.string,
	miniDeals: PropTypes.object,
	brands: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};

const hasEqualProperties = (prevProps, nextProps) => {
	const isSameTab = prevProps.tab === nextProps.tab;
	const isDisabled = prevProps.readOnly === nextProps.readOnly && prevProps.loading === nextProps.loading;
	const isBrandsLengthEqual = prevProps.brands?.length === nextProps.brands?.length;
	const isSameObject = JSON.stringify(prevProps.miniDeals) === JSON.stringify(nextProps.miniDeals);
	return isSameObject && isBrandsLengthEqual && isSameTab && isDisabled;
};

const MiniDealContainer = memo(MiniDealContainerMemo, hasEqualProperties);

export default MiniDealContainer;
